import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Subscription, interval } from 'rxjs';
import { debounceTime, startWith, switchMap, takeWhile } from 'rxjs/operators';
import { Patient } from '@models/patient';
import { FinancialEvent, FinancialSnapshot, Order } from '@models/order';
import { AuthService } from '@services/auth.service';
import { NotificationService } from '@services/notification.service';
import { PaymentService, DiscountPromo } from '@services/payment.service';
import { PatientService } from '@services/patient.service';
import { SettingsService } from '@services/settings.service';
import { FinancialCalculator } from '@classes/financial';
import { OrderDocument } from '@classes/order-document';
import { AcknowledgmentOverlayComponent } from './acknowledgment-overlay.component';
import {
  TerminalSimulatorOverlayComponent,
  TerminalSimulatorResult,
} from './terminal-simulator-overlay.component';
import { FormatData } from '@classes/format-data';
import {
  acknowledgmentChargeRows,
  isOrderAcknowledged,
} from '@classes/order-acknowledgment';

@Component({
  selector: 'app-payment-details',
  templateUrl: './payment-details.component.html',
  styleUrls: ['./payment-details.component.css'],
})
export class PaymentDetailsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() orderForm: FormGroup;
  @Input() patient: Patient;
  @Output() orderUpdated = new EventEmitter<Order>();

  historyOpen = false;
  busy = false;
  customAmount = '';
  paymentMethod = 'CASH';
  payerName = '';
  ackLanguage = '';
  terminalConfigured = false;
  terminalPaired = false;
  terminalSandbox = false;
  terminalCode = '';
  terminalStatusLabel = '';
  terminalCheckoutId = '';
  terminalSimOpen = false;
  giftCardNumber = '';
  giftCardLast4 = '';
  giftCardBalance: number | null = null;
  remoteEmail = '';
  remotePhone = '';
  lastPaymentGroup: any = null;
  paymentGroups: any[] = [];
  refundAmount = '';
  refundReason = '';
  idempotencyKey = '';
  discounts: DiscountPromo[] = [];
  availableCredit = 0;
  openInvoices: Order[] = [];
  selectedInvoiceIds: string[] = [];
  extraPatientQuery = '';
  extraPatientInvoices: Order[] = [];
  transferPatientId = '';
  transferAmount = '';
  otherCreditType = 'Custom Credit';
  otherCreditAmount = '';
  summaryRowList: { label: string; value: number }[] = [];
  openInvoiceList: Order[] = [];
  snapshot = FinancialCalculator.empty();

  private formSub: Subscription;
  private polSub: Subscription;
  private terminalPollSub: Subscription;
  private pairPollSub: Subscription;
  private readonly emptyEvents: FinancialEvent[] = [];
  private pendingTerminalPayload: any;
  private terminalSimDialog: MatDialogRef<
    TerminalSimulatorOverlayComponent,
    TerminalSimulatorResult
  >;

  constructor(
    private paymentService: PaymentService,
    private patientService: PatientService,
    private settingsService: SettingsService,
    private auth: AuthService,
    private notification: NotificationService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.settingsService.loadSettings().subscribe({ error: () => {} });
    this.paymentService.listDiscounts().subscribe((discounts) => {
      this.discounts = discounts || [];
      this.recalc(false);
    });
    this.paymentService.getAckTemplate().subscribe((template) => {
      this.ackLanguage = template?.body || '';
    });
    this.loadTerminalStatus();
    this.bindForm();
    this.loadCredit();
    this.loadOpenInvoices();
    this.loadPaymentGroups();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.orderForm && !changes.orderForm.firstChange) {
      this.bindForm();
    }
    if (changes.patient) {
      this.loadCredit();
      this.loadOpenInvoices();
      this.loadPaymentGroups();
      if (this.patient) {
        this.remoteEmail = this.remoteEmail || this.patient.email || '';
        this.remotePhone = this.remotePhone || this.patient.cell || this.patient.phone || '';
      }
    }
  }

  ngOnDestroy(): void {
    this.formSub?.unsubscribe();
    this.polSub?.unsubscribe();
    this.terminalPollSub?.unsubscribe();
    this.pairPollSub?.unsubscribe();
    this.terminalSimDialog?.close('CANCELED');
  }

  openAckOverlay(): void {
    if (!this.orderId) {
      this.notification.show(
        'error',
        'Save the order before capturing acknowledgment'
      );
      return;
    }
    // Open after this click so it is not treated as a backdrop dismiss.
    setTimeout(() => {
      const dialogRef = this.dialog.open(AcknowledgmentOverlayComponent, {
        width: '86vw',
        maxWidth: '86vw',
        height: '80vh',
        maxHeight: '80vh',
        autoFocus: false,
        panelClass: 'ack-dialog-panel',
        data: {
          language: this.ackLanguage,
          hasCompletionDate: !!this.orderForm
            ?.get('requestedCompletionDate')?.value,
          invoiceNumber: this.invoiceNumber,
          customerName: [this.patient?.firstName, this.patient?.lastName]
            .filter(Boolean)
            .join(' '),
          charges: acknowledgmentChargeRows(this.orderForm.getRawValue()),
        },
      });
      dialogRef.afterClosed().subscribe((result) => {
        const signatureDataUrl =
          typeof result === 'string' ? result : result?.signatureDataUrl;
        if (signatureDataUrl) {
          this.saveAcknowledgment(signatureDataUrl);
        }
      });
    }, 10);
  }

  get financials() {
    return this.snapshot;
  }

  get lineItems() {
    return (this.orderForm?.get('lineItems')?.value || []).filter(
      (item) => FinancialCalculator.money(item.amount) > 0
    );
  }

  get events(): FinancialEvent[] {
    return this.orderForm?.get('financialEvents')?.value || this.emptyEvents;
  }

  get discountControl(): FormControl | null {
    return this.asFormControl(this.orderForm?.get('discountId'));
  }

  get polControl(): FormControl | null {
    return this.asFormControl(this.orderForm?.get('pol'));
  }

  get completionDateControl(): FormControl | null {
    return this.asFormControl(this.orderForm?.get('requestedCompletionDate'));
  }

  get shippingControl(): FormControl | null {
    return this.asFormControl(this.orderForm?.get('products.0.shipping'));
  }

  get acknowledged(): boolean {
    return isOrderAcknowledged(this.orderForm?.getRawValue());
  }

  get orderId(): string {
    return String(this.orderForm?.get('_id')?.value || '');
  }

  get invoiceNumber(): string {
    return FormatData.displayInvoiceNumber(
      this.orderForm?.get('invoiceNumber')?.value
    );
  }

  formatInvoiceNumber(value: any): string {
    return FormatData.displayInvoiceNumber(value);
  }

  invoicePatientName(invoice: Order): string {
    const patient: any = invoice?.patient;
    if (!patient || typeof patient === 'string') {
      return '';
    }
    return [patient.lastName, patient.firstName].filter(Boolean).join(', ');
  }

  get isCustomRx(): boolean {
    return FinancialCalculator.isRxMerchandise(this.orderForm?.getRawValue());
  }

  get canPay(): boolean {
    if (!this.orderId || this.orderId === 'NEW') {
      return false;
    }
    if (!this.acknowledged) {
      return false;
    }
    if (this.isProcessing) {
      return false;
    }
    return FinancialCalculator.money(this.combinedBalance) > 0;
  }

  get isProcessing(): boolean {
    if (this.terminalCheckoutId || this.terminalSimOpen) {
      return true;
    }
    const status = String(this.lastPaymentGroup?.status || '').toUpperCase();
    return status === 'PROCESSING' || status === 'SENT';
  }

  get payInFullAmount(): number {
    const remaining = FinancialCalculator.money(this.lastPaymentGroup?.remaining);
    const status = String(this.lastPaymentGroup?.status || '').toUpperCase();
    if (remaining > 0.009 && (status === 'PARTIAL' || status === 'SENT')) {
      return remaining;
    }
    return FinancialCalculator.money(this.combinedBalance);
  }

  get combinedBalance(): number {
    const selected = this.allOpenInvoices.filter((order) =>
      this.selectedInvoiceIds.includes(String(order._id))
    );
    const others = selected.reduce(
      (sum, order) => sum + FinancialCalculator.money(order.financials?.balanceDue),
      0
    );
    if (this.selectedInvoiceIds.includes(this.orderId)) {
      return others;
    }
    return FinancialCalculator.money(this.financials.balanceDue + others);
  }

  get allOpenInvoices(): Order[] {
    return this.openInvoiceList;
  }

  formatMoney(value: any): string {
    return FinancialCalculator.formatMoney(value);
  }

  summaryRows() {
    return this.summaryRowList;
  }

  recalc(writeForm = true): void {
    if (!this.orderForm) {
      return;
    }
    const order = this.orderForm.getRawValue();
    const selected = this.discounts.find(
      (item) => String(item._id) === String(order.discountId) && item.active !== false
    );
    const snapshot = FinancialCalculator.calculate(order, {
      discount: selected,
      taxRate: this.settingsService.salesTaxRate || 0,
    });
    this.snapshot = snapshot.financials;
    this.summaryRowList = this.buildSummaryRows(snapshot.lineItems, snapshot.financials);
    if (!writeForm) {
      return;
    }
    const lineItemsCtrl = this.orderForm.get('lineItems');
    const financialsCtrl = this.orderForm.get('financials');
    if (JSON.stringify(lineItemsCtrl?.value || []) !== JSON.stringify(snapshot.lineItems)) {
      lineItemsCtrl?.setValue(snapshot.lineItems, { emitEvent: false });
    }
    if (JSON.stringify(financialsCtrl?.value || {}) !== JSON.stringify(snapshot.financials)) {
      financialsCtrl?.setValue(snapshot.financials, { emitEvent: false });
    }
  }

  payInFull(): void {
    this.submitPayment(this.payInFullAmount, true);
  }

  payCustom(): void {
    this.submitPayment(FinancialCalculator.money(this.customAmount));
  }

  submitPayment(amount: number, payInFull = false): void {
    if (!this.canPay) {
      this.notification.show('error', 'Save the order and complete acknowledgment before payment');
      return;
    }
    if (!(amount > 0)) {
      this.notification.show('error', 'Enter a payment amount');
      return;
    }
    if (this.paymentMethod === 'CREDIT CARD' || this.paymentMethod === 'GIFT CARD') {
      this.chargeTerminal(this.cappedAmount(amount), payInFull);
      return;
    }
    this.busy = true;
    const selectedOthers = this.selectedInvoiceIds.filter((id) => id && id !== this.orderId);
    const payload: any = {
      amount: FinancialCalculator.money(amount),
      payInFull: !!payInFull,
      method: this.paymentMethod,
      initials: this.initials(),
      user: this.auth.currentUser?._id,
      payer: this.payerName || this.orderForm.get('payerName')?.value,
      notes: '',
    };

    const request =
      selectedOthers.length > 0
        ? this.paymentService.allocate({
            ...payload,
            orderIds: [this.orderId, ...selectedOthers],
          })
        : this.paymentService.charge({ ...payload, orderId: this.orderId });

    request.subscribe({
      next: (result: any) => {
        this.busy = false;
        this.customAmount = '';
        const order = result?.order || (Array.isArray(result?.orders) ? result.orders[0] : result);
        if (order) {
          this.patchOrder(order);
          this.orderUpdated.emit(order);
        }
        this.loadCredit();
        this.loadOpenInvoices();
        this.applyPaymentResult(result);
        this.notification.show('success', 'Payment recorded');
      },
      error: (err) => {
        this.busy = false;
        this.notification.show(
          'error',
          err?.error?.message || 'Could not record payment'
        );
      },
    });
  }

  loadTerminalStatus(): void {
    this.paymentService.terminalStatus().subscribe((terminal) => {
      this.terminalConfigured = !!terminal?.configured;
      this.terminalPaired = !!terminal?.paired;
      this.terminalSandbox = !!terminal?.sandbox;
    });
  }

  pairTerminal(): void {
    this.busy = true;
    this.paymentService.pairTerminal().subscribe({
      next: (terminal) => {
        this.busy = false;
        this.terminalCode = terminal?.code || '';
        this.terminalStatusLabel = 'Enter this code on the Square Terminal';
        if (terminal?.paired) {
          this.terminalPaired = true;
          this.terminalCode = '';
          this.notification.show('success', 'Square Terminal paired');
          return;
        }
        if (!terminal?.pairingId) {
          this.notification.show('error', 'Could not start Square Terminal pairing');
          return;
        }
        this.pairPollSub?.unsubscribe();
        this.pairPollSub = interval(2500)
          .pipe(
            startWith(0),
            switchMap(() => this.paymentService.pollTerminalPair(terminal.pairingId)),
            takeWhile((status) => !status?.paired, true)
          )
          .subscribe({
            next: (status) => {
              this.terminalCode = status?.code || this.terminalCode;
              if (status?.paired) {
                this.terminalPaired = true;
                this.terminalSandbox = false;
                this.terminalCode = '';
                this.terminalStatusLabel = '';
                this.notification.show('success', 'Square Terminal paired');
              }
            },
            error: (err) => {
              this.notification.show(
                'error',
                err?.error?.message || 'Could not finish Square Terminal pairing'
              );
            },
          });
      },
      error: (err) => {
        this.busy = false;
        this.notification.show(
          'error',
          err?.error?.message || 'Could not start Square Terminal pairing'
        );
      },
    });
  }

  cancelTerminalCheckout(): void {
    if (this.terminalSimOpen) {
      this.cancelSimulatedTerminal();
      return;
    }
    const checkoutId = this.terminalCheckoutId;
    this.terminalPollSub?.unsubscribe();
    if (!checkoutId) {
      this.busy = false;
      this.terminalStatusLabel = '';
      return;
    }
    this.paymentService.cancelTerminalCheckout(checkoutId).subscribe({
      next: () => {
        this.busy = false;
        this.terminalCheckoutId = '';
        this.terminalStatusLabel = '';
        this.notification.show('success', 'Square Terminal checkout canceled');
      },
      error: (err) => {
        this.busy = false;
        this.terminalCheckoutId = '';
        this.terminalStatusLabel = '';
        this.notification.show(
          'error',
          err?.error?.message || 'Could not cancel the Square Terminal checkout'
        );
      },
    });
  }

  cancelSimulatedTerminal(): void {
    const dialog = this.terminalSimDialog;
    this.terminalSimDialog = null;
    this.terminalSimOpen = false;
    this.busy = false;
    this.terminalStatusLabel = '';
    this.pendingTerminalPayload = null;
    if (dialog) {
      dialog.close('CANCELED');
    }
  }

  private openTerminalSimulator(amount: number): void {
    this.busy = true;
    this.terminalSimOpen = true;
    this.terminalStatusLabel = 'Waiting for the customer on the Square Terminal…';
    setTimeout(() => {
      this.terminalSimDialog = this.dialog.open(TerminalSimulatorOverlayComponent, {
        data: { amount },
        width: '360px',
        maxWidth: '92vw',
        disableClose: true,
        autoFocus: false,
        panelClass: 'terminal-sim-dialog-panel',
      });
      this.terminalSimDialog.afterClosed().subscribe((result) => {
        this.terminalSimDialog = null;
        if (result === 'APPROVED' || result === 'DECLINED') {
          this.finishSimulatedTerminal(result);
          return;
        }
        this.terminalSimOpen = false;
        this.busy = false;
        this.terminalStatusLabel = '';
        this.pendingTerminalPayload = null;
      });
    }, 10);
  }

  private finishSimulatedTerminal(result: Extract<TerminalSimulatorResult, 'APPROVED' | 'DECLINED'>): void {
    if (!this.pendingTerminalPayload) {
      this.cancelSimulatedTerminal();
      return;
    }
    this.busy = true;
    this.paymentService
      .simulateTerminalPayment({
        ...this.pendingTerminalPayload,
        result,
      })
      .subscribe({
        next: (res) => {
          this.busy = false;
          this.terminalSimOpen = false;
          this.terminalStatusLabel = '';
          this.customAmount = '';
          this.pendingTerminalPayload = null;
          const order = res?.order || (Array.isArray(res?.orders) ? res.orders[0] : null);
          if (order) {
            this.patchOrder(order);
            this.orderUpdated.emit(order);
          }
          this.loadCredit();
          this.loadOpenInvoices();
          this.applyPaymentResult(res);
          this.notification.show('success', 'Payment recorded');
        },
        error: (err) => {
          this.busy = false;
          this.notification.show(
            'error',
            err?.error?.message || 'Could not finish the simulated Terminal payment'
          );
          if (result !== 'DECLINED') {
            this.terminalSimOpen = false;
            this.terminalStatusLabel = '';
            this.pendingTerminalPayload = null;
          }
        },
      });
  }

  private chargeTerminal(amount: number, payInFull: boolean): void {
    if (!this.terminalPaired) {
      this.notification.show(
        'error',
        'Pair the Square Terminal before taking a card payment'
      );
      return;
    }
    this.pendingTerminalPayload = this.buildPaymentPayload(amount, payInFull);
    if (this.terminalSandbox) {
      this.openTerminalSimulator(this.pendingTerminalPayload.amount);
      return;
    }
    this.busy = true;
    this.terminalStatusLabel = 'Processing — waiting on the Square Terminal…';
    this.paymentService.startTerminalCheckout(this.pendingTerminalPayload).subscribe({
      next: (checkout) => {
        this.terminalCheckoutId = checkout?.checkoutId || '';
        this.rememberPaymentGroup(checkout?.paymentGroup || checkout);
        if (checkout?.paymentGroupId) {
          this.pendingTerminalPayload.paymentGroupId = checkout.paymentGroupId;
        }
        if (!this.terminalCheckoutId) {
          this.busy = false;
          this.terminalStatusLabel = '';
          this.notification.show('error', 'Could not start the Square Terminal checkout');
          return;
        }
        this.pollTerminalCheckout();
      },
      error: (err) => {
        this.busy = false;
        this.terminalStatusLabel = '';
        this.notification.show(
          'error',
          err?.error?.message || 'Could not start the Square Terminal checkout'
        );
      },
    });
  }

  private pollTerminalCheckout(): void {
    this.terminalPollSub?.unsubscribe();
    this.terminalPollSub = interval(2000)
      .pipe(
        startWith(0),
        switchMap(() =>
          this.paymentService.pollTerminalCheckout(
            this.terminalCheckoutId,
            this.pendingTerminalPayload
          )
        ),
        takeWhile((res) => {
          const status = String(res?.status || '').toUpperCase();
          return (
            status !== 'COMPLETED' &&
            status !== 'CANCELED' &&
            status !== 'CANCELLED' &&
            status !== 'TIMED_OUT' &&
            status !== 'FAILED'
          );
        }, true)
      )
      .subscribe({
        next: (res) => {
          const status = String(res?.status || '').toUpperCase();
          this.rememberPaymentGroup(res?.paymentGroup || res);
          if (status === 'COMPLETED') {
            this.busy = false;
            this.terminalCheckoutId = '';
            this.customAmount = '';
            this.idempotencyKey = '';
            const order = res?.order || (Array.isArray(res?.orders) ? res.orders[0] : null);
            if (order) {
              this.patchOrder(order);
              this.orderUpdated.emit(order);
            }
            this.loadCredit();
            this.loadOpenInvoices();
            this.applyPaymentResult(res);
            this.notification.show('success', 'Payment recorded');
            this.terminalStatusLabel = '';
          } else if (
            status === 'CANCELED' ||
            status === 'CANCELLED' ||
            status === 'TIMED_OUT' ||
            status === 'FAILED'
          ) {
            this.busy = false;
            this.terminalCheckoutId = '';
            this.idempotencyKey = '';
            this.terminalStatusLabel = '';
            const label =
              status === 'TIMED_OUT'
                ? 'Square Terminal checkout timed out'
                : status === 'FAILED'
                ? 'Square Terminal checkout failed'
                : 'Square Terminal checkout was canceled';
            this.notification.show('error', label);
          } else {
            this.terminalStatusLabel = 'Processing — waiting on the Square Terminal…';
          }
        },
        error: (err) => {
          this.busy = false;
          this.terminalCheckoutId = '';
          this.terminalStatusLabel = '';
          this.notification.show(
            'error',
            err?.error?.message || 'Could not finish the Square Terminal checkout'
          );
        },
      });
  }

  applyCredit(): void {
    if (!this.orderId) {
      return;
    }
    this.busy = true;
    this.paymentService
      .applyStoreCredit({
        orderId: this.orderId,
        initials: this.initials(),
        user: this.auth.currentUser?._id,
      })
      .subscribe({
        next: (order) => {
          this.busy = false;
          this.patchOrder(order);
          this.orderUpdated.emit(order);
          this.loadCredit();
          this.notification.show('success', 'Store credit applied');
        },
        error: (err) => {
          this.busy = false;
          this.notification.show('error', err?.error?.message || 'Could not apply credit');
        },
      });
  }

  transferCredit(): void {
    const destinationPatientId = String(this.transferPatientId || '').trim();
    const amount = FinancialCalculator.money(this.transferAmount);
    if (!this.patient?._id || !destinationPatientId || !(amount > 0)) {
      this.notification.show('error', 'Enter a destination patient and amount');
      return;
    }
    this.busy = true;
    this.paymentService
      .transferStoreCredit({
        sourcePatientId: this.patient._id,
        destinationPatientId,
        amount,
        initials: this.initials(),
        user: this.auth.currentUser?._id,
        sourceInvoice: this.invoiceNumber,
      })
      .subscribe({
        next: () => {
          this.busy = false;
          this.transferAmount = '';
          this.transferPatientId = '';
          this.loadCredit();
          this.notification.show('success', 'Store credit transferred');
        },
        error: (err) => {
          this.busy = false;
          this.notification.show(
            'error',
            err?.error?.message || 'Could not transfer credit'
          );
        },
      });
  }

  applyOtherCredit(): void {
    const amount = FinancialCalculator.money(this.otherCreditAmount);
    if (!this.orderId || !(amount > 0)) {
      this.notification.show('error', 'Enter a credit amount');
      return;
    }
    this.busy = true;
    this.paymentService
      .applyStructuredCredit({
        orderId: this.orderId,
        type: this.otherCreditType,
        amount,
        initials: this.initials(),
        user: this.auth.currentUser?._id,
        sourceInvoice: this.invoiceNumber,
      })
      .subscribe({
        next: (order) => {
          this.busy = false;
          this.otherCreditAmount = '';
          this.patchOrder(order);
          this.orderUpdated.emit(order);
          this.notification.show('success', 'Credit applied');
        },
        error: (err) => {
          this.busy = false;
          this.notification.show(
            'error',
            err?.error?.message || 'Could not apply credit'
          );
        },
      });
  }

  saveAcknowledgment(signatureDataUrl?: string): void {
    if (!this.orderId) {
      this.notification.show('error', 'Save the order before capturing acknowledgment');
      return;
    }
    if (!signatureDataUrl) {
      this.notification.show('error', 'Customer signature is required');
      return;
    }
    this.busy = true;
    this.paymentService
      .acknowledge({
        orderId: this.orderId,
        language: this.ackLanguage,
        signatureDataUrl,
        requestedCompletionDate: this.orderForm.get('requestedCompletionDate')?.value,
        initials: this.initials(),
        user: this.auth.currentUser?._id,
      })
      .subscribe({
        next: (order) => {
          this.busy = false;
          this.patchOrder(order);
          this.orderUpdated.emit(order);
          this.notification.show('success', 'Order acknowledged');
        },
        error: (err) => {
          this.busy = false;
          this.notification.show(
            'error',
            err?.error?.message || 'Could not save acknowledgment'
          );
        },
      });
  }

  printSummary(): void {
    this.recalc(false);
    OrderDocument.printHtml(this.buildSummaryHtml());
  }

  emailSummary(): void {
    const email = String(this.patient?.email || '').trim();
    const f = this.financials;
    const invoice = this.invoiceNumber || 'your order';
    const body = [
      `Order summary for invoice ${invoice}.`,
      `Order total: $${this.formatMoney(f.orderTotal)}`,
      `Paid: $${this.formatMoney(f.paid)}`,
      `Balance due: $${this.formatMoney(f.balanceDue)}`,
    ].join('\n');
    OrderDocument.openMailto(email, invoice, '');
    const subject = encodeURIComponent(`Order summary ${invoice}`);
    window.location.href = `mailto:${encodeURIComponent(email)}?subject=${subject}&body=${encodeURIComponent(body)}`;
  }

  toggleInvoice(id: string, event: { checked?: boolean }): void {
    const invoiceId = String(id);
    const checked = !!event?.checked;
    if (checked) {
      if (!this.selectedInvoiceIds.includes(invoiceId)) {
        this.selectedInvoiceIds = [...this.selectedInvoiceIds, invoiceId];
      }
    } else {
      this.selectedInvoiceIds = this.selectedInvoiceIds.filter((item) => item !== invoiceId);
    }
  }

  searchExtraPatient(): void {
    const query = String(this.extraPatientQuery || '').trim();
    if (!query) {
      return;
    }
    this.paymentService.openInvoices(query).subscribe((orders) => {
      this.extraPatientInvoices = orders || [];
      this.refreshOpenInvoiceList();
    });
  }

  private escapeHtml(value: any): string {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  private buildSummaryHtml(): string {
    const order = this.orderForm?.getRawValue() || {};
    const productElement = order.products?.[0] || {};
    const product = productElement.product || {};
    const vendor = product.vendor?.name || product.vendor || '';
    const item = [vendor, product.model, product.color, product.size]
      .map((part) => String(part || '').trim())
      .filter(Boolean)
      .join(' · ');
    const patientName = [this.patient?.lastName, this.patient?.firstName]
      .filter(Boolean)
      .join(', ');
    const storeName = this.auth.getStoreObject?.()?.storeName || '';
    const invoice = this.invoiceNumber || order.invoiceNumber || '';
    const ackAt = order.acknowledgedAt
      ? new Date(order.acknowledgedAt).toLocaleString()
      : '';
    const rows = (this.summaryRowList || [])
      .map(
        (row) =>
          `<tr><td>${this.escapeHtml(row.label)}</td><td>$${this.formatMoney(
            row.value
          )}</td></tr>`
      )
      .join('');
    const history = (this.events || [])
      .map((event) => `<li>${this.escapeHtml(this.eventLabel(event))}</li>`)
      .join('');
    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Order Summary ${this.escapeHtml(invoice)}</title>
  <style>
    body { font-family: Helvetica, Arial, sans-serif; color: #111; padding: 32px; }
    h1 { font-size: 20px; letter-spacing: 0.08em; margin: 0 0 8px; }
    .meta { color: #555; margin: 0 0 4px; }
    table { width: 100%; max-width: 420px; border-collapse: collapse; margin: 20px 0; }
    td { padding: 6px 0; border-bottom: 1px solid #ddd; }
    td:last-child { text-align: right; font-variant-numeric: tabular-nums; }
    ul { padding-left: 18px; }
    @media print { body { padding: 0; } }
  </style>
</head>
<body>
  <h1>ORDER SUMMARY</h1>
  <p class="meta">${this.escapeHtml(storeName)}</p>
  <p class="meta">${this.escapeHtml(patientName)}</p>
  <p class="meta">Invoice ${this.escapeHtml(invoice)}</p>
  ${item ? `<p class="meta">${this.escapeHtml(item)}</p>` : ''}
  ${ackAt ? `<p class="meta">Acknowledged ${this.escapeHtml(ackAt)}</p>` : ''}
  <table>${rows}</table>
  ${history ? `<h2 style="font-size:14px;">Payment history</h2><ul>${history}</ul>` : ''}
</body>
</html>`;
  }

  eventLabel(event: FinancialEvent): string {
    const when = event.date ? new Date(event.date) : null;
    const stamp = when
      ? `${when.toLocaleDateString()} — ${when.toLocaleTimeString([], {
          hour: 'numeric',
          minute: '2-digit',
        })}`
      : '';
    const parts = [stamp, event.type];
    if (event.method) {
      parts.push(event.method);
    }
    if (event.amount) {
      parts.push(`$${this.formatMoney(event.amount)}`);
    }
    if (event.initials) {
      parts.push(event.initials);
    }
    if (event.payer) {
      parts.push(event.payer);
    }
    if (event.sourceInvoice) {
      parts.push(`Source Inv #${event.sourceInvoice}`);
    }
    if (event.squarePaymentId) {
      parts.push(`Square ${event.squarePaymentId}`);
    }
    if (event.paymentGroupId) {
      parts.push(`Group ${event.paymentGroupId}`);
    }
    return parts.filter(Boolean).join(' — ');
  }

  checkGiftCardBalance(): void {
    const gan = String(this.giftCardNumber || '').replace(/\s+/g, '');
    if (!gan) {
      this.notification.show('error', 'Enter the gift card number to check the balance');
      return;
    }
    this.busy = true;
    this.paymentService.checkGiftCard(gan).subscribe({
      next: (card) => {
        this.busy = false;
        this.giftCardNumber = '';
        this.giftCardLast4 = card?.ganLast4 || gan.slice(-4);
        this.giftCardBalance = FinancialCalculator.money(card?.balance);
        this.notification.show(
          'success',
          `Gift card ${this.giftCardLast4} has $${this.formatMoney(this.giftCardBalance)}`
        );
      },
      error: (err) => {
        this.busy = false;
        this.notification.show(
          'error',
          err?.error?.message || 'Could not look up that Square gift card'
        );
      },
    });
  }

  sendRemotePayment(channel: 'email' | 'sms'): void {
    if (!this.orderId || this.orderId === 'NEW' || !this.acknowledged) {
      this.notification.show('error', 'Save the order and complete acknowledgment before payment');
      return;
    }
    const amount = this.customAmount
      ? this.cappedAmount(FinancialCalculator.money(this.customAmount))
      : this.payInFullAmount;
    const payload = this.buildPaymentPayload(amount, !this.customAmount);
    payload.email = this.remoteEmail || this.patient?.email || '';
    this.busy = true;
    this.paymentService.startRemotePayment(payload).subscribe({
      next: (res: any) => {
        this.busy = false;
        const group = res?.paymentGroup || res;
        const url = res?.url || group?.paymentLinkUrl || '';
        this.rememberPaymentGroup(group);
        if (!url) {
          this.notification.show('error', 'Square did not return a payment link');
          return;
        }
        const invoice = this.invoiceNumber || 'your order';
        const body = `Please pay invoice ${invoice}: ${url}`;
        if (channel === 'email') {
          const email = String(this.remoteEmail || this.patient?.email || '').trim();
          const subject = encodeURIComponent(`Payment request ${invoice}`);
          window.location.href = `mailto:${encodeURIComponent(
            email
          )}?subject=${subject}&body=${encodeURIComponent(body)}`;
        } else {
          const phone = String(this.remotePhone || this.patient?.cell || this.patient?.phone || '')
            .replace(/[^\d+]/g, '');
          const sep = /iPhone|iPad|iPod/i.test(navigator.userAgent) ? '&' : '?';
          window.location.href = `sms:${encodeURIComponent(phone)}${sep}body=${encodeURIComponent(
            body
          )}`;
        }
        this.notification.show('success', 'Payment request is ready to send');
      },
      error: (err) => {
        this.busy = false;
        this.notification.show(
          'error',
          err?.error?.message || 'Could not create the Square payment link'
        );
      },
    });
  }

  cancelRemotePayment(): void {
    const id = this.lastPaymentGroup?._id;
    if (!id) {
      return;
    }
    this.busy = true;
    this.paymentService.cancelRemotePayment(id).subscribe({
      next: (res: any) => {
        this.busy = false;
        this.idempotencyKey = '';
        this.rememberPaymentGroup(res?.paymentGroup);
        this.notification.show('success', 'Payment request canceled');
      },
      error: (err) => {
        this.busy = false;
        this.notification.show(
          'error',
          err?.error?.message || 'Could not cancel the payment request'
        );
      },
    });
  }

  refundLastGroup(): void {
    const group = this.lastRefundableGroup;
    const amount = FinancialCalculator.money(this.refundAmount);
    if (!group?._id || !(amount > 0)) {
      this.notification.show('error', 'Enter a refund amount for the original Square payment');
      return;
    }
    this.busy = true;
    this.paymentService
      .refundPaymentGroup(group._id, {
        amount,
        reason: this.refundReason,
        initials: this.initials(),
        user: this.auth.currentUser?._id,
      })
      .subscribe({
        next: (res: any) => {
          this.busy = false;
          this.refundAmount = '';
          this.refundReason = '';
          const order = Array.isArray(res?.orders) ? res.orders[0] : res?.order;
          if (order) {
            this.patchOrder(order);
            this.orderUpdated.emit(order);
          }
          this.applyPaymentResult(res);
          this.loadCredit();
          this.loadOpenInvoices();
          this.notification.show('success', 'Refund recorded');
        },
        error: (err) => {
          this.busy = false;
          this.notification.show(
            'error',
            err?.error?.message || 'Could not refund this payment'
          );
        },
      });
  }

  get lastRefundableGroup(): any {
    return (this.paymentGroups || []).find((group) =>
      (group.tenders || []).some(
        (tender: any) =>
          String(tender.status).toUpperCase() === 'COMPLETED' && tender.squarePaymentId
      )
    );
  }

  allocationLines(group = this.lastPaymentGroup): { invoice: string; amount: number }[] {
    return (group?.allocations || []).map((row: any) => ({
      invoice: this.formatInvoiceNumber(row.invoiceNumber),
      amount: FinancialCalculator.money(row.applied || row.amount),
    }));
  }

  private bindForm(): void {
    this.formSub?.unsubscribe();
    this.polSub?.unsubscribe();
    if (!this.orderForm) {
      return;
    }
    this.payerName = this.orderForm.get('payerName')?.value || '';
    this.recalc(false);
    this.formSub = this.orderForm.valueChanges
      .pipe(debounceTime(0))
      .subscribe(() => this.recalc(true));
    const polControl = this.orderForm.get('pol');
    if (polControl) {
      this.polSub = polControl.valueChanges.subscribe((checked) => {
        const lensPol = this.orderForm.get('products.0.lens.POL');
        if (lensPol) {
          lensPol.setValue(checked ? 'POL' : '', { emitEvent: false });
        }
      });
    }
  }

  private loadCredit(): void {
    const patientId = this.patient?._id;
    if (!patientId) {
      this.availableCredit = 0;
      return;
    }
    this.paymentService.availableCredit(patientId).subscribe({
      next: (res: any) => {
        this.availableCredit = FinancialCalculator.money(res?.available);
      },
      error: () => {
        this.availableCredit = 0;
      },
    });
  }

  private loadOpenInvoices(): void {
    const patientId = this.patient?._id;
    if (!patientId) {
      this.openInvoices = [];
      this.refreshOpenInvoiceList();
      return;
    }
    this.paymentService.openInvoices(patientId).subscribe((orders) => {
      this.openInvoices = orders || [];
      this.refreshOpenInvoiceList();
    });
    this.patientService
      .linkedInvoices(String(patientId), this.orderId)
      .subscribe((orders) => {
        this.extraPatientInvoices = orders || [];
        this.refreshOpenInvoiceList();
      });
  }

  private refreshOpenInvoiceList(): void {
    const current = (this.openInvoices || []).filter(
      (order) => String(order._id) !== this.orderId
    );
    this.openInvoiceList = current.concat(this.extraPatientInvoices || []);
  }

  private patchOrder(order: Order): void {
    if (!this.orderForm || !order) {
      return;
    }
    this.orderForm.get('financials')?.setValue(order.financials, { emitEvent: false });
    this.orderForm.get('lineItems')?.setValue(order.lineItems || [], { emitEvent: false });
    this.orderForm.get('financialEvents')?.setValue(order.financialEvents || [], { emitEvent: false });
    this.orderForm.get('acknowledgedAt')?.setValue(order.acknowledgedAt || null, { emitEvent: false });
    this.orderForm.get('acknowledgment')?.setValue(order.acknowledgment || null, { emitEvent: false });
    this.orderForm.get('paidInFullDate')?.setValue(order.paidInFullDate || null, { emitEvent: false });
    this.orderForm.get('payerName')?.setValue(order.payerName || this.payerName, { emitEvent: false });
    this.recalc(false);
  }

  private buildSummaryRows(
    lineItems: { category?: string; amount?: number }[],
    financials: FinancialSnapshot
  ): { label: string; value: number }[] {
    const rows = (lineItems || [])
      .filter((item) => FinancialCalculator.money(item.amount) > 0)
      .map((item) => ({
        label: item.category,
        value: item.amount,
      }));
    rows.push({ label: 'SUBTOTAL', value: financials.subtotal });
    if (financials.discount > 0) {
      rows.push({ label: 'DISCOUNT / PROMO', value: financials.discount });
    }
    if (financials.tax > 0) {
      rows.push({ label: 'TAX', value: financials.tax });
    }
    if (financials.postage > 0) {
      rows.push({ label: 'POSTAGE', value: financials.postage });
    }
    rows.push({ label: 'ORDER TOTAL', value: financials.orderTotal });
    if (financials.creditsApplied > 0) {
      rows.push({ label: 'CREDITS', value: financials.creditsApplied });
    }
    rows.push({ label: 'PAID', value: financials.paid });
    rows.push({ label: 'BALANCE DUE', value: financials.balanceDue });
    return rows;
  }

  private asFormControl(control: AbstractControl | null | undefined): FormControl | null {
    return control ? (control as FormControl) : null;
  }

  private cappedAmount(amount: number): number {
    let next = FinancialCalculator.money(amount);
    if (this.paymentMethod === 'GIFT CARD' && this.giftCardBalance != null) {
      next = Math.min(next, FinancialCalculator.money(this.giftCardBalance));
    }
    return FinancialCalculator.money(next);
  }

  private nextIdempotencyKey(): string {
    if (!this.idempotencyKey) {
      this.idempotencyKey =
        (typeof crypto !== 'undefined' && crypto.randomUUID && crypto.randomUUID()) ||
        `${Date.now()}-${Math.random()}`;
    }
    return this.idempotencyKey;
  }

  private buildPaymentPayload(amount: number, payInFull: boolean): any {
    const selectedOthers = this.selectedInvoiceIds.filter((id) => id && id !== this.orderId);
    const remainingGroup =
      this.lastPaymentGroup &&
      ['PARTIAL', 'PROCESSING', 'SENT'].includes(
        String(this.lastPaymentGroup.status || '').toUpperCase()
      )
        ? this.lastPaymentGroup
        : null;
    return {
      amount: FinancialCalculator.money(amount),
      payInFull: !!payInFull,
      method: this.paymentMethod,
      orderId: this.orderId,
      orderIds: [this.orderId, ...selectedOthers],
      initials: this.initials(),
      user: this.auth.currentUser?._id,
      payer: this.payerName || this.orderForm.get('payerName')?.value,
      notes:
        this.paymentMethod === 'GIFT CARD'
          ? this.giftCardLast4
            ? `Square gift card ${this.giftCardLast4}`
            : 'Square gift card'
          : this.terminalSandbox
          ? 'Square Terminal (sandbox)'
          : 'Square Terminal',
      idempotencyKey: this.nextIdempotencyKey(),
      paymentGroupId: remainingGroup?._id || '',
    };
  }

  private rememberPaymentGroup(group: any): void {
    if (!group) {
      return;
    }
    const normalized = group.paymentGroup || group;
    if (!normalized._id && !normalized.groupKey) {
      return;
    }
    this.lastPaymentGroup = normalized;
  }

  private applyPaymentResult(result: any): void {
    const group = result?.paymentGroup || result?.checkout?.paymentGroup;
    this.rememberPaymentGroup(group);
    if (this.lastPaymentGroup && FinancialCalculator.money(this.lastPaymentGroup.remaining) > 0.009) {
      this.idempotencyKey = '';
    } else {
      this.idempotencyKey = '';
    }
    this.loadPaymentGroups();
  }

  private loadPaymentGroups(): void {
    if (!this.orderId || this.orderId === 'NEW') {
      this.paymentGroups = [];
      return;
    }
    this.paymentService.listOrderPaymentGroups(this.orderId).subscribe({
      next: (groups) => {
        this.paymentGroups = groups || [];
        if (!this.lastPaymentGroup && this.paymentGroups.length) {
          this.lastPaymentGroup = this.paymentGroups[0];
        }
      },
      error: () => {
        this.paymentGroups = [];
      },
    });
  }

  private initials(): string {
    return (
      String(this.orderForm?.get('initials')?.value || '').trim() ||
      this.auth.currentUser?.initials ||
      ''
    );
  }
}
